import React from 'react';
import { useSelector } from 'react-redux';
import { Users, User, UserPlus, Check, ChevronDown } from 'lucide-react';
import {
  canIncludeColleagues,
  canViewAgencyWide,
  isAgentUser,
  seesAgencyWideByDefault,
} from '../../utils/roleAccess';

// Staff who carry an 'agent' role but are not client-facing sales agents, so they
// must never be offerable as a colleague to fold in. Wally Gaynor is on the
// media/tech team; his account is kept as an agent for access reasons yet he owns
// no leads or sends worth surfacing here (Michael Piccoli / Joshua Diokno, Monday
// 12164535066). Matched on email so it survives a name change or account rebuild.
const PICKER_EXCLUDED_EMAILS = new Set(['wally@collings.com.au']);

/**
 * "All agents / My records" — the one scope switch for surfaces that can show
 * either the whole office or just you.
 *
 * WHY THIS EXISTS. Admin used to mean "sees the whole agency", so the pipeline,
 * the dashboard tiles and the Sent Contracts panel all quietly showed an admin
 * every agent's rows under a heading that said "my". Simon Abbott, 31 Aug 2026:
 * "can we please completely separate my CRM from other agents? I think because
 * I'm admin I'm getting other agents appraisals in my pipeline", and on Sent
 * Contracts: "please only just make it that individual agents can only see
 * there own sent contracts".
 *
 * So the office view now belongs to the agency principal (super admin) alone.
 * Christian Gravias, 7 Sep 2026, Monday 12984138077: "Please only make it that
 * super admin can see everything and the agents only see there own data." A
 * plain admin and a PA no longer see this switch either; an admin covering for
 * a colleague uses the "+ Include an agent" picker below, and a PA gets the
 * desk they assist on. Agents have never had anything but their own rows.
 *
 * This mirrors the switcher the Prospecting Database already grew for the same
 * complaint, so the two read the same way.
 */

const storageKey = (surface) => `scope.agencyWide.${surface}`;

/**
 * Scope state for one surface.
 *
 * @param surface  stable key, e.g. 'pipeline' — namespaces the saved choice so
 *                 the pipeline and the dashboard remember separately.
 * @returns { agencyWide, setAgencyWide, canSwitch, canIncludeAgents }
 *          `canSwitch` is true for the agency principal only; everyone else's
 *          `agencyWide` is always false, so render the switch only when it is
 *          true. `canIncludeAgents` is the wider admin tier and gates the
 *          "+ Include an agent" picker (pass it to `useExtraAgents`).
 */
export const useAgencyScope = (surface) => {
  const { user } = useSelector((state) => state.auth);
  const canSwitch = canViewAgencyWide(user);
  const canIncludeAgents = canIncludeColleagues(user);
  const fallback = seesAgencyWideByDefault(user);

  const [agencyWide, setStored] = React.useState(() => {
    if (!canSwitch) return false;
    try {
      const saved = localStorage.getItem(storageKey(surface));
      if (saved === 'true') return true;
      if (saved === 'false') return false;
    } catch {
      // Private browsing and locked-down profiles throw on localStorage. The
      // switch still works for the session, it just will not be remembered.
    }
    return fallback;
  });

  // The user object arrives after the first render on a cold load, so the
  // initial state above can be computed against a null user. Re-resolve once
  // the role is actually known, but never clobber a saved choice.
  React.useEffect(() => {
    if (!canSwitch) {
      setStored(false);
      return;
    }
    try {
      if (localStorage.getItem(storageKey(surface)) !== null) return;
    } catch {
      /* no stored preference is readable — fall through to the role default */
    }
    setStored(fallback);
  }, [canSwitch, fallback, surface]);

  const setAgencyWide = React.useCallback(
    (next) => {
      setStored(next);
      try {
        localStorage.setItem(storageKey(surface), String(next));
      } catch {
        /* not remembering the choice is not worth breaking the click over */
      }
      // Anything else reading this surface's scope has to move with the switch,
      // not on next reload - the sidebar badge for the pipeline boards reads it
      // too, and a badge counting a different board than the page draws is the
      // bug we just fixed (Christian Gravias, Monday 12944444309).
      try {
        window.dispatchEvent(
          new CustomEvent('agency-scope-changed', { detail: { surface, agencyWide: next } })
        );
      } catch {
        /* no CustomEvent in this environment (jsdom without it, older shells) */
      }
    },
    [surface]
  );

  // The other side of that broadcast: every hook instance on this surface,
  // wherever it is mounted, follows the one the user actually clicked.
  React.useEffect(() => {
    const onScopeChange = (event) => {
      if (event?.detail?.surface !== surface) return;
      setStored(Boolean(event.detail.agencyWide));
    };
    window.addEventListener('agency-scope-changed', onScopeChange);
    return () => window.removeEventListener('agency-scope-changed', onScopeChange);
  }, [surface]);

  return { agencyWide: canSwitch && agencyWide, setAgencyWide, canSwitch, canIncludeAgents };
};

const AgencyScopeSwitch = ({
  agencyWide,
  onChange,
  mineLabel = 'My records',
  agencyLabel = 'All agents',
  className = '',
}) => (
  <div
    className={`inline-flex items-center rounded-lg bg-gray-100 p-0.5 ${className}`}
    role="group"
    aria-label="Whose records to show"
  >
    <button
      type="button"
      onClick={() => onChange(false)}
      aria-pressed={!agencyWide}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
        agencyWide ? 'text-gray-500 hover:text-gray-700' : 'bg-white text-gray-900 shadow-sm'
      }`}
    >
      <User className="w-3 h-3" />
      {mineLabel}
    </button>
    <button
      type="button"
      onClick={() => onChange(true)}
      aria-pressed={agencyWide}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
        agencyWide ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      <Users className="w-3 h-3" />
      {agencyLabel}
    </button>
  </div>
);


/* ------------------------------------------------------------------ *
 * "…and this colleague's too"
 *
 * All agents / My records is all-or-nothing, and a desk is often not.
 * Simon Abbott, 3 Sep 2026: "Please ensure I only have mine and Gennevas so I
 * can start to sort through it all." So a scoped surface can also carry a
 * short list of other agents to fold in alongside your own rows. It only
 * applies to the "My records" side - the agency view already has everybody.
 * ------------------------------------------------------------------ */

const EMPTY_IDS = [];

const extraAgentsKey = (surface) => `scope.extraAgents.${surface}`;

const readExtraAgents = (surface) => {
  try {
    const saved = JSON.parse(localStorage.getItem(extraAgentsKey(surface)) || '[]');
    return Array.isArray(saved) ? saved.map(Number).filter(Boolean) : [];
  } catch {
    return [];
  }
};

/**
 * The colleagues folded into this surface's "My records" view.
 *
 * @param surface  the same key passed to useAgencyScope.
 * @param enabled  false for anyone not allowed to see past their own desk;
 *                 the ids are then always empty, whatever is in storage.
 */
export const useExtraAgents = (surface, enabled = true) => {
  const [stored, setStored] = React.useState(() => readExtraAgents(surface));

  const setAgentIds = React.useCallback(
    (next) => {
      const ids = [...new Set((next || []).map(Number).filter(Boolean))];
      setStored(ids);
      try {
        localStorage.setItem(extraAgentsKey(surface), JSON.stringify(ids));
      } catch {
        /* not remembering the choice is not worth breaking the click over */
      }
    },
    [surface]
  );

  const toggleAgentId = React.useCallback(
    (id) => {
      const numeric = Number(id);
      setAgentIds(
        stored.includes(numeric) ? stored.filter((x) => x !== numeric) : [...stored, numeric]
      );
    },
    [stored, setAgentIds]
  );

  // Memoised: this array ends up in the dependency list of the callbacks that
  // refetch pages and sidebar badges, and a fresh [] on every render would make
  // those callbacks change identity forever.
  const agentIds = React.useMemo(() => (enabled ? stored : EMPTY_IDS), [enabled, stored]);

  return { agentIds, setAgentIds, toggleAgentId };
};

/**
 * Tick-box dropdown of colleagues to include alongside your own records.
 *
 * `agents` is whatever the page already loaded from role-management/agents/;
 * the signed-in user is dropped from the list because "mine" is never optional.
 */
export const IncludeAgentsPicker = ({
  agents = [],
  selectedIds = [],
  onToggle,
  onClear,
  currentUserId,
  className = '',
}) => {
  const [open, setOpen] = React.useState(false);
  const boxRef = React.useRef(null);

  React.useEffect(() => {
    const onClickOutside = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selectable = agents
    .filter((a) => a && a.id && Number(a.id) !== Number(currentUserId))
    .filter((a) => a.is_active !== false && !a.is_archived)
    // Only actual agents can be folded in beside your own rows - this is "cover
    // for a colleague", and admins / super admins / dev / support accounts don't
    // own leads or sends to show (Joshua Diokno, Monday 12164535066). The list
    // is sourced from role-management/agents/, which returns all staff, so filter
    // to agents here rather than narrowing that shared endpoint.
    .filter((a) => isAgentUser(a))
    .filter((a) => !PICKER_EXCLUDED_EMAILS.has(String(a.email || '').trim().toLowerCase()))
    .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));

  const chosen = selectable.filter((a) => selectedIds.includes(Number(a.id)));
  const label = chosen.length
    ? `+ ${chosen.map((a) => (a.name || a.email || '').split(' ')[0]).join(', ')}`
    : 'Include an agent';

  return (
    <div className={`relative ${className}`} ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-lg border transition-colors ${
          chosen.length
            ? 'border-[#55C5D0]/40 bg-[#f0fafb] text-[#2c8c95]'
            : 'border-gray-200 bg-white text-gray-500 hover:text-gray-700'
        }`}
      >
        <UserPlus className="w-3 h-3" />
        <span className="max-w-[180px] truncate">{label}</span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-1 w-64 rounded-xl border border-gray-100 bg-white shadow-lg py-1 max-h-72 overflow-y-auto">
          <p className="px-3 py-2 text-[11px] text-gray-400 leading-snug">
            Shown alongside your own records.
          </p>
          {selectable.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-400">No other agents to show.</p>
          )}
          {selectable.map((agent) => {
            const ticked = selectedIds.includes(Number(agent.id));
            return (
              <button
                key={agent.id}
                type="button"
                onClick={() => onToggle(agent.id)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-gray-700 hover:bg-gray-50"
              >
                <span
                  className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                    ticked ? 'bg-[#55C5D0] border-[#55C5D0]' : 'border-gray-300'
                  }`}
                >
                  {ticked && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                <span className="truncate">{agent.name || agent.email}</span>
              </button>
            );
          })}
          {chosen.length > 0 && (
            <button
              type="button"
              onClick={() => { onClear(); setOpen(false); }}
              className="w-full text-left px-3 py-2 mt-1 text-[11px] font-semibold text-gray-400 hover:text-gray-600 border-t border-gray-100"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AgencyScopeSwitch;
